import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import pricingService from "../../services/pricingService";
import api from "services/api";

// ===== PRICING RULE THUNKS =====

/**
 * Create pricing rule
 */
export const createPricingRuleThunk = createAsyncThunk(
  "pricing/createRule",
  async (ruleData, { rejectWithValue }) => {
    try {
      const response = await pricingService.createPricingRule(ruleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get pricing rules with filters
 */
export const getPricingRulesThunk = createAsyncThunk(
  "pricing/getRules",
  async (params, { rejectWithValue }) => {
    try {
      const response = await pricingService.getPricingRules(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Get pricing rule by ID
 */
export const getPricingRuleByIdThunk = createAsyncThunk(
  "pricing/getRuleById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await pricingService.getPricingRuleById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Update pricing rule
 */
export const updatePricingRuleThunk = createAsyncThunk(
  "pricing/updateRule",
  async ({ id, ruleData }, { rejectWithValue }) => {
    try {
      const response = await pricingService.updatePricingRule(id, ruleData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Delete pricing rule
 */
export const deletePricingRuleThunk = createAsyncThunk(
  "pricing/deleteRule",
  async (id, { rejectWithValue }) => {
    try {
      const response = await pricingService.deletePricingRule(id);
      return { ...response.data, deletedId: id };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Clone pricing rule
 */
export const clonePricingRuleThunk = createAsyncThunk(
  "pricing/cloneRule",
  async ({ id, name }, { rejectWithValue }) => {
    try {
      const response = await pricingService.clonePricingRule(id, name);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Toggle pricing rule status
 */
export const togglePricingRuleStatusThunk = createAsyncThunk(
  "pricing/toggleRuleStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await pricingService.togglePricingRuleStatus(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ===== PRICE CALCULATION THUNKS =====

/**
 * Calculate pricing for single product
 */
export const calculatePricingThunk = createAsyncThunk(
  "pricing/calculate",
  async (pricingData, { rejectWithValue }) => {
    try {
      const response = await pricingService.calculatePricing(pricingData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Calculate bulk pricing
 */
export const calculateBulkPricingThunk = createAsyncThunk(
  "pricing/calculateBulk",
  async (bulkData, { rejectWithValue }) => {
    try {
      const response = await pricingService.calculateBulkPricing(bulkData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

/**
 * Calculate price with offers applied
 * Note: This is a convenience method. Backend handles offer validation.
 */
export const calculatePriceWithOffersThunk = createAsyncThunk(
  "pricing/calculateWithOffers",
  async ({ product, branchCode, offerCodes, options }, { rejectWithValue }) => {
    try {
      const response = await pricingService.calculatePriceWithOffers(
        product,
        branchCode,
        offerCodes,
        options
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message
      );
    }
  }
);

// ✅ NEW: Get pricing rule analytics
export const getPricingRuleAnalyticsThunk = createAsyncThunk(
  "pricing/getPricingRuleAnalytics",
  async ({ ruleId, dateRange = "30d" }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/pricing/rules/${ruleId}/analytics`, {
        params: { dateRange },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch analytics"
      );
    }
  }
);

// ✅ NEW: Get pricing rule audit log
export const getPricingRuleAuditLogThunk = createAsyncThunk(
  "pricing/getPricingRuleAuditLog",
  async (
    { ruleId, page = 1, limit = 50, action = "all", dateRange = "30d" },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(`/pricing/rules/${ruleId}/audit-log`, {
        params: { page, limit, action, dateRange },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch audit log"
      );
    }
  }
);

// ===== ACTIONS =====

export const clearPricingError = createAction("pricing/clearError");
export const clearPricingCalculation = createAction("pricing/clearCalculation");
export const clearBulkCalculation = createAction(
  "pricing/clearBulkCalculation"
);
export const resetPricingState = createAction("pricing/resetState");
export const setCurrentRule = createAction("pricing/setCurrentRule");
export const clearCurrentRule = createAction("pricing/clearCurrentRule");
